import { FC, useEffect, useMemo, useState } from 'react';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { useAuth } from 'react-oidc-context';
import { useSignIn } from '../../../hooks/auth';
import { Link, useLocation } from 'react-router-dom';
import { APP_URLS, INITIAL_INBOX_TIMEOUT } from '../../../consts';
import BurgerMenuIcon from './../../../assets/Itv/Burger Menu.svg';
import BurgerMenuIconMobile from './../../../assets/Itv/Burger Menu-mobile.svg';
import { ReactComponent as ProfileIcon } from './../../../assets/Itv/PROFILE.svg';
import { ReactComponent as MessagesIcon } from './../../../assets/Itv/bell.svg';
import { Button } from '../../shared/Button';
import { useDispatch, useSelector } from 'react-redux';
import { setIsNavigationExpanded } from '../../../state/app/reducers';
import { isNavigationExpanded } from '../../../state/app';
import Cookies from 'js-cookie';
import { setCashierModal } from '../../../state/cashier/reducers';
import { CashierType } from '../../../state/cashier';
import { sessionPreviousLocation } from '../../../utils/storage';
import { useUserBalance } from '../../../hooks/useUserBalance';
import { formatCurrency } from '../../../utils/wallets';
import { useBrandedNavigate } from '../../../hooks/useBrandedNavigate';
import { ItvMessages } from '../ItvMessages';
import { handleItvMessagesBadge } from '../ItvMessages/helpers';
import { NativeEventBus } from '../../../native/NativeEventBus';
import { ItvStarBalance } from '../ItvBalance';
import './ItvSubHeader.scss';

const isNativeApp = NativeEventBus.isNativeBridgeAvailable();

export const ItvSubHeader: FC = () => {
  const { isAuthenticated } = useAuth();
  const signIn = useSignIn();
  const { navigate, registrationUrl } = useBrandedNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isNavExpanded = useSelector(isNavigationExpanded);
  const { userBalance, isBalanceLoading } = useUserBalance();
  const nativeEventBus = useMemo(() => new NativeEventBus(), []);

  const [showMsgPrompt, setShowMsgPrompt] = useState(false);
  const [msgCount, setMsgCount] = useState<number | undefined>();

  const isMobileAndTablet = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);
  const isBelowTabletLandWidth = useBreakpointInfo([MediaBreakpoint.BELOW_TABLET]);

  useEffect(() => {
    // The timeout is used because of xtremepush delay due to user's account switch
    if (!isNativeApp) {
      setTimeout(() => handleItvMessagesBadge(setMsgCount), INITIAL_INBOX_TIMEOUT);
    }
  }, []);

  useEffect(() => {
    if (!isNativeApp) return;

    const handler = (event: MessageEvent): void => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        const badge = data.payload?.badge ?? data.badge;

        if (badge !== undefined) setMsgCount(badge);
      } catch (e) {
        console.error('Failed to parse native event', e);
      }
    };

    nativeEventBus.listenToEvent('getInboxBadge', handler);

    setTimeout(() => {
      nativeEventBus.getInboxBadge();
    }, INITIAL_INBOX_TIMEOUT);

    return () => {
      nativeEventBus.removeListener('getInboxBadge', handler);
    };
  }, [nativeEventBus]);

  const onBurgerClick = (): void => {
    dispatch(setIsNavigationExpanded(!isNavExpanded));

    const isMainMenuHidden = Cookies.get('isMainMenuHidden');

    !isBelowTabletLandWidth && Cookies.set('isMainMenuHidden', isMainMenuHidden === 'true' ? 'false' : 'true');
  };

  const handleLogin = (): void => {
    signIn();
  };

  const handleJoin = (): void => navigate(registrationUrl);

  const onDepositBtnClick = (): void => {
    dispatch(setCashierModal({ isOpen: true, type: CashierType.Deposit }));
  };

  const handleProfileClick = (): void => {
    // Store current location in session storage like NavigationLink does
    sessionPreviousLocation.set({ value: { previousLocation: location.pathname } });
  };

  return (
    <>
      <div className="sub-header">
        <div className="sub-header__controls">
          {/* Don't change sub-header_hamburger class as it impacts sidebar close functionality */}
          <img
            className="sub-header__control sub-header__hamburger"
            onClick={onBurgerClick}
            src={isMobileAndTablet ? BurgerMenuIconMobile : BurgerMenuIcon}
            alt="burger menu icon"
          />
          {isAuthenticated && (
            <>
              <div className="sub-header__message-icon" onClick={(): void => setShowMsgPrompt(true)}>
                <MessagesIcon className="sub-header__control sub-header__icon" aria-hidden="true" />
                {Boolean(msgCount) && <div className="sub-header__message-counter">{msgCount}</div>}
              </div>

              {!isMobileAndTablet && (
                <Link to={APP_URLS.myAccount.home} onClick={handleProfileClick}>
                  <ProfileIcon className="sub-header__control sub-header__icon" aria-hidden="true" />
                </Link>
              )}

              <ItvStarBalance />
            </>
          )}
        </div>

        <div className="sub-header__actions">
          {isAuthenticated ? (
            <>
              <div className="sub-header__action sub-header__action--balance">
                <span className="sub-header__balance-text">Balance:</span>
                <span className="sub-header__balance-value">
                  {isBalanceLoading ? '...' : formatCurrency(userBalance.mainCashBalance)?.replace(' ', '')}
                </span>
              </div>
              <Button
                type="button"
                className="sub-header__action-btn sub-header__action-bnt--deposit"
                onClick={onDepositBtnClick}
              >
                Deposit
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                onClick={handleLogin}
                className="sub-header__action-btn sub-header__action-bnt--join"
              >
                Login
              </Button>
              <Button
                type="button"
                onClick={handleJoin}
                className="sub-header__action-btn sub-header__action-bnt--login"
              >
                Join
              </Button>
            </>
          )}
        </div>
      </div>
      {showMsgPrompt && (
        <ItvMessages isOpen setIsOpen={setShowMsgPrompt} setMsgCount={setMsgCount} msgCount={msgCount} />
      )}
    </>
  );
};
